/**
 * Tipo de rostro y forma de dientes que suele acompañarlo.
 * La regla clásica del diseño de sonrisa (Williams) dice que la forma del incisivo central se parece al rostro visto al revés: rostro cuadrado,
 * dientes más cuadrados; rostro ovalado, dientes ovoides; rostro que se afina hacia el mentón, dientes más triangulares. Es una GUÍA de partida:
 * la edad, el sexo, la personalidad y lo que el paciente desea pesan igual o más, y el profesional decide.
 *
 * El tipo se calcula con cuatro distancias de la cara (altura, pómulos, frente y mandíbula) comparadas con prototipos. Los prototipos son valores
 * de referencia razonables, no una norma validada: por eso Donna muestra el segundo tipo más parecido y deja elegir otro.
 */
import type { FormaDiente } from "./sonrisa.ts";

export type TipoRostro = "redondo" | "cuadrado" | "rectangular" | "ovalado" | "corazon" | "pera" | "diamante";
export const TIPOS_ROSTRO: readonly TipoRostro[] = ["ovalado", "redondo", "cuadrado", "rectangular", "corazon", "pera", "diamante"];
export const NOMBRE_ROSTRO: Readonly<Record<TipoRostro, string>> = {
  ovalado: "Ovalado", redondo: "Redondo", cuadrado: "Cuadrado", rectangular: "Rectangular (alargado)", corazon: "Corazón (frente ancha y mentón fino)",
  pera: "Pera (mandíbula más ancha que la frente)", diamante: "Diamante (pómulos marcados)",
};
const DESCRIPCION: Readonly<Record<TipoRostro, string>> = {
  ovalado: "Proporciones equilibradas: la cara es más larga que ancha y la frente y la mandíbula son parecidas, con la mandíbula algo más angosta.",
  redondo: "Largo y ancho parecidos, con contornos suaves y la mandíbula poco marcada.",
  cuadrado: "Largo y ancho parecidos, con la frente y la mandíbula casi igual de anchas y la mandíbula marcada.",
  rectangular: "Cara larga y angosta, con la frente y la mandíbula casi igual de anchas.",
  corazon: "Frente ancha que se afina hacia un mentón angosto.",
  pera: "Mandíbula más ancha que la frente.",
  diamante: "Los pómulos son lo más ancho; la frente y la mandíbula son más angostas.",
};
/** Forma de dientes que suele acompañar a cada rostro (regla clásica; es una guía). */
export const FORMA_POR_ROSTRO: Readonly<Record<TipoRostro, FormaDiente>> = {
  ovalado: "ovoide", redondo: "ovoide", cuadrado: "cuadrada", rectangular: "cuadrada", corazon: "triangular", pera: "cuadrada", diamante: "ovoide",
};

/** Distancias de la cara en píxeles (o en cualquier unidad, solo importan las proporciones). */
export interface MedidasRostro { altura: number; pomulos: number; frente: number; mandibula: number }

// Prototipos: [alto/pómulos, frente/pómulos, mandíbula/pómulos]. El alto va de la parte alta de la frente al mentón.
const PROTOTIPOS: Readonly<Record<TipoRostro, readonly [number, number, number]>> = {
  ovalado: [1.30, 0.80, 0.72], redondo: [1.12, 0.82, 0.80], cuadrado: [1.16, 0.84, 0.88], rectangular: [1.40, 0.84, 0.88],
  corazon: [1.28, 0.90, 0.66], pera: [1.20, 0.74, 0.88], diamante: [1.30, 0.72, 0.70],
};
const PESOS = [1.0, 1.4, 1.4] as const;

export interface ClasificacionRostro {
  tipo: TipoRostro;
  /** el segundo tipo más parecido (si no está muy lejos del primero) */
  segundo: TipoRostro | null;
  proporciones: { altoPomulos: number; frentePomulos: number; mandibulaPomulos: number };
  formaSugerida: FormaDiente;
  explicacion: string;
}

export function clasificarRostro(m: MedidasRostro): ClasificacionRostro | null {
  if (![m.altura, m.pomulos, m.frente, m.mandibula].every((n) => Number.isFinite(n) && n > 0)) return null;
  const v = [m.altura / m.pomulos, m.frente / m.pomulos, m.mandibula / m.pomulos] as const;
  const dist = (t: TipoRostro) => Math.sqrt(PROTOTIPOS[t].reduce((s, p, i) => s + PESOS[i] * (v[i] - p) ** 2, 0));
  const orden = [...TIPOS_ROSTRO].sort((a, b) => dist(a) - dist(b));
  const [primero, segundo] = orden;
  const cercano = dist(segundo) - dist(primero) < 0.05;
  const r2 = (n: number) => Math.round(n * 100) / 100;
  const forma = FORMA_POR_ROSTRO[primero];
    return {
    tipo: primero, segundo: cercano ? segundo : null,
    proporciones: { altoPomulos: r2(v[0]), frentePomulos: r2(v[1]), mandibulaPomulos: r2(v[2]) },
    formaSugerida: forma,
    explicacion: `${NOMBRE_ROSTRO[primero]}. ${DESCRIPCION[primero]} Con la regla clásica, la forma de dientes que suele acompañarlo es la ${forma}.${cercano ? ` Se parece también a un rostro ${NOMBRE_ROSTRO[segundo].split(" (")[0].toLowerCase()}.` : ""}`,
  };
}

export const AVISO_ROSTRO = "El tipo de rostro se calcula con la foto y es orientativo (la cabeza debe estar de frente y recta). La forma de los dientes también depende de la edad, el sexo, la personalidad y lo que el paciente desea: elige tú.";
